import { AlertTriangle, Lock } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { formatTimeAgo } from "@/shared/utils/dateTimeHelpers";

type ConfirmReleaseModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  /** True when an admin is taking the lock away from another operator instead of releasing their own. */
  isForce?: boolean;
  /**
   * Current holder of the lock. Only shown on the force variant, where the admin needs to know whom they cut
   * off.
   */
  takerName?: string | null;
  /** When the lock was taken. Shown next to the holder: a fresh lock is a much worse thing to take away. */
  takenAt?: string | null;
};

/**
 * Confirmation step for handing a payment back to the queue.
 *
 * Every other state change on the board (take, cancel, mark as paid) is confirmed, and releasing is the only
 * one that can affect a different operator's work, so it gets a confirmation too. The force variant is
 * deliberately louder: it names the holder and uses a destructive action button.
 */
export const ConfirmReleaseModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  isForce = false,
  takerName,
  takenAt,
}: ConfirmReleaseModalProps) => {
  const { t, activeLanguage } = useTranslations("payments");
  const takenAgo = takenAt ? formatTimeAgo(takenAt, { locale: activeLanguage }) : "";

  const title = isForce ? t("modal_release_admin_title") : t("modal_release_title");
  const description = isForce ? t("modal_release_admin_description") : t("modal_release_description");
  const confirmLabel = isForce ? t("modal_release_admin_confirm") : t("modal_release_confirm");

  return (
    <ColocaDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isLoading) onClose();
      }}
      title={title}
      description={description}
      size="default"
      showCloseButton={false}
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <ColocaButton variant="ghost" onClick={onClose} disabled={isLoading}>
            {t("modal_release_back")}
          </ColocaButton>
          <ColocaButton
            variant={isForce ? "destructive" : "primary"}
            onClick={onConfirm}
            loading={isLoading}
            disabled={isLoading}
          >
            {isLoading ? t("modal_release_processing") : confirmLabel}
          </ColocaButton>
        </div>
      }
    >
      {isForce && (
        <div className="space-y-3">
          {takerName && (
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2.5 text-sm">
              <Lock className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <span className="min-w-0 truncate text-foreground">
                {t("modal_release_admin_holder", { name: takerName })}
              </span>
              {takenAgo && (
                <>
                  <span className="text-muted-foreground" aria-hidden>
                    ·
                  </span>
                  <span className="shrink-0 text-muted-foreground">{takenAgo}</span>
                </>
              )}
            </div>
          )}
          <div className="flex items-start gap-3 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{t("modal_release_admin_warning")}</span>
          </div>
        </div>
      )}
    </ColocaDialog>
  );
};
